import {useMemo, useState} from "react";
import _ from "lodash";
import {useAtomValue} from "jotai";
import {useTranslation} from "react-i18next";
import {Box, Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import {MainViewLayout} from "../MainViewLayout";
import {InsightsOverviewCard} from "./InsightsOverviewCard";
import {CompanyHeader} from "../companies/CompanyHeader";
import {FieldsInsightsTable} from "./FieldsInsightsTable";
import {useFarmsInsightsItems} from "../../hooks/fieldInsightsItemsHooks";
import {routerCompanyIdAtom} from "../../store/routerStore";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {FieldCreateDialog} from "../dialogs/FieldCreateDialog";
import {DataDownloadDialog} from "../dialogs/DataDownloadDialog";
import {DataUploadDialog} from "../dialogs/DataUploadDialog";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useActiveCompanyOrders} from "../../hooks/ordersHooks";
import {DataIcon} from "../icons/DataIcon";
import {isMaderaCompany} from "../../utils/maderaUtils";
import {MaderaFarmUnitTable} from "../madera/FarmUnitTable";
import {useCompany} from "../../hooks/companiesHooks";
import {useActiveCompanyOverviewData} from "../../hooks/companiesHooks";
import {queryClient} from "../../utils/queryClient";
import {invalidateAllCompanyFarmsHooks} from "../../hooks/farmsHooks";

export const CompanyInsightsView: React.FC = () => {
    const {t} = useTranslation();
    const companyId = useAtomValue(routerCompanyIdAtom);
    const company = useCompany();
    const isMadera = company?.data && isMaderaCompany(company?.data);
    const farmInsightsItems = useFarmsInsightsItems();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const [createNewAnchorEl, setCreateNewAnchorEl] = useState<null | HTMLElement>(null);
    const [dataToolsAnchorEl, setDataToolsAnchorEl] = useState<null | HTMLElement>(null);
    const [farmCreateDialogOpened, setFarmCreateDialogOpened] = useState<boolean>(false);
    const [fieldCreateDialogOpened, setFieldCreateDialogOpened] = useState<boolean>(false);
    const [dataDownloadDialogOpened, setDataDownloadDialogOpened] = useState<boolean>(false);
    const [dataUploadDialogOpened, setDataUploadDialogOpened] = useState<boolean>(false);
    const {refresh: refreshOrders} = useActiveCompanyOrders();
    const overviewData = useActiveCompanyOverviewData();

    const onFarmCreateDialogClose = (isSuccessful: boolean) => {
        setFarmCreateDialogOpened(false);
        if (isSuccessful && companyId) {
            invalidateAllCompanyFarmsHooks(companyId);
        }
        setCreateNewAnchorEl(null);
    };

    const onFieldCreateDialogClose = (isSuccessful: boolean) => {
        setFieldCreateDialogOpened(false);
        if (isSuccessful && companyId) {
            invalidateAllCompanyFarmsHooks(companyId);
            void queryClient.invalidateQueries({queryKey: ["companyFields", companyId]});
            refreshOrders();
        }
        setCreateNewAnchorEl(null);
    };

    const onDataDownloadDialogClose = () => {
        setDataDownloadDialogOpened(false);
        setDataToolsAnchorEl(null);
    };

    const onDataUploadDialogClose = () => {
        setDataUploadDialogOpened(false);
        setDataToolsAnchorEl(null);
    };

    const hasOverviewData = useMemo(() => {
        return !!(overviewData.isSuccess && overviewData.data.crops.length);
    }, [overviewData]);

    return (
        <MainViewLayout header={<CompanyHeader basePath="/insights" />}>
            <Stack direction="column" gap="1.5rem">
                <Stack direction="column">
                    {isMadera && (
                        <Stack direction="column">
                            <MaderaFarmUnitTable />
                        </Stack>
                    )}
                    <Stack direction="row" height="4rem" alignItems="center">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("insights.company_insights_view.overview")}
                        </Typography>
                    </Stack>
                    <Box padding="1.5rem" bgcolor="white">
                        {hasOverviewData ? (
                            <InsightsOverviewCard
                                overviewData={overviewData.isSuccess ? overviewData.data : undefined}
                                isLoading={overviewData.isLoading}
                            />
                        ) : (
                            <Typography>{t("insights.company_insights_view.no_fields_or_active_fields")}</Typography>
                        )}
                    </Box>
                </Stack>
                <Stack direction="column">
                    <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("insights.company_insights_view.farm_fields")}
                        </Typography>
                        <Stack direction="row" gap="1rem">
                            {(access.canCreateNewField || access.canCreateNewField) && (
                                <Stack>
                                    <Button
                                        startIcon={<AddIcon />}
                                        size="large"
                                        onClick={(e) => setCreateNewAnchorEl(e.currentTarget)}
                                        endIcon={<ArrowDownIcon />}
                                    >
                                        {t("companies.farms_view.create")}
                                    </Button>
                                    <Menu
                                        id="create-new-menu"
                                        anchorEl={createNewAnchorEl}
                                        open={!!createNewAnchorEl}
                                        onClose={() => setCreateNewAnchorEl(null)}
                                    >
                                        {access.canCreateNewFarm && (
                                            <MenuItem onClick={() => setFarmCreateDialogOpened(true)}>
                                                <ListItemIcon>
                                                    <AddIcon />
                                                </ListItemIcon>
                                                {t("companies.farms_view.new_farm")}
                                            </MenuItem>
                                        )}
                                        {access.canCreateNewField && (
                                            <MenuItem onClick={() => setFieldCreateDialogOpened(true)}>
                                                <ListItemIcon>
                                                    <AddIcon />
                                                </ListItemIcon>
                                                {t("companies.farms_view.new_field")}
                                            </MenuItem>
                                        )}
                                    </Menu>
                                </Stack>
                            )}
                            <Stack>
                                <Button
                                    startIcon={
                                        <DataIcon
                                            sx={{
                                                stroke: "#303A8C",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                                paddingRight: "0.2rem",
                                            }}
                                        />
                                    }
                                    size="large"
                                    onClick={(e) => setDataToolsAnchorEl(e.currentTarget)}
                                    endIcon={<ArrowDownIcon />}
                                >
                                    {t("data_tools.name")}
                                </Button>
                                <Menu
                                    id="data-tools-menu"
                                    anchorEl={dataToolsAnchorEl}
                                    open={!!dataToolsAnchorEl}
                                    onClose={() => setDataToolsAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => setDataDownloadDialogOpened(true)}>
                                        <ListItemIcon>
                                            <DownloadIcon />
                                        </ListItemIcon>
                                        {t("data_tools.download")}
                                    </MenuItem>
                                    {access.canUploadCompanyData && (
                                        <MenuItem onClick={() => setDataUploadDialogOpened(true)}>
                                            <ListItemIcon>
                                                <UploadIcon />
                                            </ListItemIcon>
                                            {t("data_tools.add")}
                                        </MenuItem>
                                    )}
                                </Menu>
                            </Stack>
                        </Stack>
                    </Stack>
                    <FieldsInsightsTable
                        insightsItems={farmInsightsItems ?? []}
                        companyId={companyId}
                        onRefresh={() => {
                            if (companyId) {
                                invalidateAllCompanyFarmsHooks(companyId);
                            }
                        }}
                        canEditItem={access.canEditCompanyFarms}
                    />
                </Stack>
            </Stack>

            {farmCreateDialogOpened && companyId && (
                <FarmCreateDialog
                    mode="new"
                    companyId={companyId}
                    open={farmCreateDialogOpened}
                    onClose={onFarmCreateDialogClose}
                />
            )}

            {fieldCreateDialogOpened && companyId && (
                <FieldCreateDialog
                    companyId={companyId}
                    open={fieldCreateDialogOpened}
                    mode="new"
                    onClose={onFieldCreateDialogClose}
                />
            )}

            {dataDownloadDialogOpened && companyId && (
                <DataDownloadDialog
                    open={dataDownloadDialogOpened}
                    mode="company"
                    companyId={companyId}
                    onClose={onDataDownloadDialogClose}
                />
            )}

            {dataUploadDialogOpened && companyId && (
                <DataUploadDialog open={dataUploadDialogOpened} onClose={onDataUploadDialogClose} />
            )}
        </MainViewLayout>
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, [
    "canEditCompanyFarms",
    "canCreateNewField",
    "canCreateNewFarm",
    "canUploadCompanyData",
]);
